import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Paper,
  Typography,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { categoryAPI, colorAPI } from '../services/api';
import { Category, Color } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const Settings: React.FC = () => {
  const queryClient = useQueryClient();
  const [tabValue, setTabValue] = useState(0);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);

  const categoryForm = useForm<{ name: string }>();
  const colorForm = useForm<{ name: string; hexCode: string }>();

  // 카테고리 목록 조회
  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: async () => {
      const response = await categoryAPI.getAll();
      return response.data || [];
    },
  });

  // 색상 목록 조회
  const { data: colors } = useQuery({
    queryKey: ['colors'],
    queryFn: async () => {
      const response = await colorAPI.getAll();
      return response.data || [];
    },
  });

  // 카테고리 생성
  const createCategoryMutation = useMutation({
    mutationFn: categoryAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setCategoryOpen(false);
      categoryForm.reset();
    },
  });

  // 카테고리 삭제
  const deleteCategoryMutation = useMutation({
    mutationFn: categoryAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
  });

  // 색상 생성
  const createColorMutation = useMutation({
    mutationFn: colorAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colors'] });
      setColorOpen(false);
      colorForm.reset();
    },
  });

  // 색상 삭제
  const deleteColorMutation = useMutation({
    mutationFn: colorAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['colors'] });
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleDeleteCategory = (id: number) => {
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      deleteCategoryMutation.mutate(id);
    }
  };

  const handleDeleteColor = (id: number) => {
    if (window.confirm('정말로 삭제하시겠습니까?')) {
      deleteColorMutation.mutate(id);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>설정</Typography>

      <Paper sx={{ p: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="카테고리 관리" />
          <Tab label="색상 관리" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">카테고리 목록</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCategoryOpen(true)}
            >
              카테고리 추가
            </Button>
          </Box>

          <List>
            {categories?.map((category) => (
              <ListItem key={category.id} divider>
                <ListItemText primary={category.name} />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleDeleteCategory(category.id)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">색상 목록</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setColorOpen(true)}
            >
              색상 추가
            </Button>
          </Box>

          <Grid container spacing={2}>
            {colors?.map((color) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={color.id}>
                <Paper
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <CircleIcon sx={{ color: color.hexCode, fontSize: 40 }} />
                    <Box>
                      <Typography variant="subtitle1">{color.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {color.hexCode}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton onClick={() => handleDeleteColor(color.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </TabPanel>
      </Paper>

      {/* 카테고리 추가 다이얼로그 */}
      <Dialog open={categoryOpen} onClose={() => setCategoryOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={categoryForm.handleSubmit((data) => createCategoryMutation.mutate(data))}>
          <DialogTitle>카테고리 추가</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="카테고리명"
              fullWidth
              required
              {...categoryForm.register('name', { required: true })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCategoryOpen(false)}>취소</Button>
            <Button type="submit" variant="contained">추가</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* 색상 추가 다이얼로그 */}
      <Dialog open={colorOpen} onClose={() => setColorOpen(false)} maxWidth="xs" fullWidth>
        <form onSubmit={colorForm.handleSubmit((data) => createColorMutation.mutate(data))}>
          <DialogTitle>색상 추가</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="색상명"
              fullWidth
              required
              {...colorForm.register('name', { required: true })}
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="헥스 코드"
              fullWidth
              required
              placeholder="#7FCDCD"
              {...colorForm.register('hexCode', {
                required: true,
                pattern: {
                  value: /^#[0-9A-Fa-f]{6}$/,
                  message: '올바른 헥스 코드 형식이 아닙니다 (#RRGGBB)',
                },
              })}
              helperText={colorForm.formState.errors.hexCode?.message}
              error={!!colorForm.formState.errors.hexCode}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setColorOpen(false)}>취소</Button>
            <Button type="submit" variant="contained">추가</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Settings; 